import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, Brain, Shield, Sparkles, ArrowRight, 
  CheckCircle2, ExternalLink, BookOpen, Mic, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const fadds = [
  {
    letter: 'F',
    word: 'Fear',
    description: 'The paralysis that stops us from taking action. Fear tells us we\'re not ready, not good enough, or that failure is certain.',
    overcome: 'Acknowledge fear as a signal, not a stop sign. Take small steps forward despite feeling afraid.',
    color: 'from-red-500 to-rose-500'
  },
  {
    letter: 'A',
    word: 'Anxiety',
    description: 'The worry about what might happen. Anxiety keeps us focused on hypothetical futures instead of present opportunities.',
    overcome: 'Ground yourself in the present. Focus on what you can control today.',
    color: 'from-amber-500 to-orange-500'
  },
  {
    letter: 'D',
    word: 'Doubt',
    description: 'The voice that questions our abilities and decisions. Doubt undermines confidence and leads to second-guessing.',
    overcome: 'Build evidence of your capabilities through action. Celebrate small wins.',
    color: 'from-blue-500 to-indigo-500'
  },
  {
    letter: 'D',
    word: 'Depression',
    description: 'The weight that makes everything feel harder. Depression can drain energy and make forward progress seem impossible.',
    overcome: 'Seek support. Small consistent actions can rebuild momentum. You don\'t have to face this alone.',
    color: 'from-violet-500 to-purple-500'
  }
];

const grindFramework = [
  { letter: 'G', word: 'Greatness', description: 'Recognize your potential for excellence' },
  { letter: 'R', word: 'Requires', description: 'Understand that achievement takes effort' },
  { letter: 'I', word: 'Intense', description: 'Commit to focused, passionate work' },
  { letter: 'N', word: 'NonStop', description: 'Maintain persistence through challenges' },
  { letter: 'D', word: 'Drive', description: 'Let purpose fuel your journey' },
];

const resources = [
  {
    title: 'The Book of Barz',
    description: 'Amazon #1 Bestseller exploring Social-Emotional Learning through poetry and lived experience.',
    link: 'https://www.amazon.com/Book-Barz-Keep-GodWork/dp/B087LB9GGH',
    type: 'Book'
  },
  {
    title: 'SEL Essentials Curriculum',
    description: 'Downloadable workshop resources for educators and mentors.',
    link: 'https://www.kavontecarthron.com/sel-resources',
    type: 'Download'
  },
  {
    title: 'Book KaVonte to Speak',
    description: 'Bring the FADDs of Life message to your school, organization, or event.',
    link: 'https://www.kavontecarthron.com/contact',
    type: 'Speaking'
  }
];

const faddsSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "FADDs of Life - Fear, Anxiety, Doubt, and Depression",
  "description": "Learn about the FADDs of Life framework created by KaVonte Carthron. Understanding and overcoming Fear, Anxiety, Doubt, and Depression through Social-Emotional Learning.",
  "author": {
    "@type": "Person",
    "name": "KaVonte Carthron"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Fearless IT",
    "url": "https://fearlessit.io"
  }
};

const FADDsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="fadds-page">
      <SEO 
        title="FADDs of Life - Understanding Fear, Anxiety, Doubt & Depression"
        description="Learn about the FADDs of Life framework created by KaVonte Carthron. Practical tools for understanding and overcoming Fear, Anxiety, Doubt, and Depression through Social-Emotional Learning."
        keywords="FADDs of Life, fear anxiety doubt depression, social emotional learning, KaVonte Carthron, mental wellness, overcoming anxiety, youth mentorship, GRIND framework"
        url="/resources/fadds"
        structuredData={faddsSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full mb-6">
            <Heart className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">Mental Wellness</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="fadds-title">
            FADDs of Life
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #7C3AED, #A855F7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Understanding & Overcoming Life's Challenges
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            A framework created by KaVonte Carthron to help people recognize and overcome the emotional challenges that hold them back from achieving their potential.
          </p>
        </div>
      </section>

      {/* What is FADDs Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              What Are the FADDs?
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Four emotional challenges that everyone faces at some point in their journey. Recognizing them is the first step to overcoming them.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {fadds.map((fadd, idx) => (
              <div key={idx} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${fadd.color}`}></div>
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${fadd.color} flex items-center justify-center`}>
                      <span className="text-2xl font-bold text-white">{fadd.letter}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-carbon-black">{fadd.word}</h3>
                  </div>
                  <p className="text-slate-gray mb-4 leading-relaxed">{fadd.description}</p>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-semibold text-green-600 uppercase tracking-wider">How to Overcome</span>
                        <p className="text-sm text-carbon-black mt-1">{fadd.overcome}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GRIND Framework Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
              <Sparkles className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-700">The Solution</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              The GRIND Framework
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Greatness Requires Intense NonStop Drive. The framework for pushing through FADDs and achieving your potential.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {grindFramework.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-center min-w-[200px]">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-white">{item.letter}</span>
                </div>
                <div className="text-lg font-bold text-carbon-black mb-1">{item.word}</div>
                <div className="text-sm text-slate-gray">{item.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Helps Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-6">
                Who This Helps
              </h2>
              <div className="space-y-4 text-slate-gray leading-relaxed">
                <p>
                  The FADDs framework was developed through KaVonte Carthron's work with students, entrepreneurs, and professionals. It speaks to anyone who has ever felt stuck, scared, or unsure of their next step.
                </p>
                <p>
                  Whether you're a teenager navigating school pressures, an entrepreneur facing business challenges, or anyone working through personal obstacles, understanding FADDs can help you move forward.
                </p>
              </div>
              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-purple-500" />
                  <span className="text-carbon-black">Students & Young Adults</span>
                </div>
                <div className="flex items-center gap-3">
                  <Brain className="h-5 w-5 text-purple-500" />
                  <span className="text-carbon-black">Entrepreneurs & Professionals</span>
                </div>
                <div className="flex items-center gap-3">
                  <Heart className="h-5 w-5 text-purple-500" />
                  <span className="text-carbon-black">Educators & Mentors</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-8 border border-purple-100">
              <blockquote className="text-lg text-carbon-black leading-relaxed italic mb-4">
                "FADDs are not just obstacles; they're signals. They tell us where our growth edges are. When you learn to work with them instead of against them, everything changes."
              </blockquote>
              <div className="text-sm text-purple-600 font-medium">- KaVonte Carthron</div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Learn More
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Explore resources to dive deeper into the FADDs framework and Social-Emotional Learning.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {resources.map((resource, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  {resource.type === 'Book' && <BookOpen className="h-5 w-5 text-purple-500" />}
                  {resource.type === 'Download' && <Shield className="h-5 w-5 text-purple-500" />}
                  {resource.type === 'Speaking' && <Mic className="h-5 w-5 text-purple-500" />}
                  <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider">
                    {resource.type}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-carbon-black mb-2">{resource.title}</h3>
                <p className="text-sm text-slate-gray mb-4 leading-relaxed">{resource.description}</p>
                <a 
                  href={resource.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-purple-600 font-medium hover:text-purple-700 transition-colors"
                >
                  Learn More
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Heart className="h-12 w-12 text-purple-500 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Support Youth Through GodWork Foundation
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            The FADDs of Life framework is at the heart of GodWork Foundation's youth programs. Learn how you can help empower the next generation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/philanthropy">
              <Button className="bg-gradient-to-r from-purple-500 to-violet-500 text-white px-8 py-6 text-lg font-semibold rounded-xl hover:from-purple-600 hover:to-violet-600 transition-all w-full sm:w-auto">
                Our Philanthropy
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a 
              href="https://donorbox.org/godworkfoundation"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-purple-400 text-purple-600 hover:bg-purple-50 transition-all w-full sm:w-auto">
                Donate to GodWork
                <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default FADDsPage;
